import type { PictureFrame } from "~/model.ts";
import type { Money } from "@brickme/project-core/src";
import type { CartItem } from "~/features/order/store-slice.ts";
import type { GiftRedemptionState } from "../store-slice.ts";

type GiftRedemptionParams = {
	sessionId: string;
	giftFramingVariantId?: string;
	giftFramingVariantQty?: number;
};

/**
 * Find a frame that contains a specific gift framing variant ID
 */
function findFrameWithGiftVariant(
	frames: readonly PictureFrame[],
	giftFramingVariantId: string,
): PictureFrame | undefined {
	return frames.find((frame) => {
		const hasGiftVariant = frame.variants?.some((variant) => {
			return variant.productVariantId === giftFramingVariantId;
		});
		return hasGiftVariant;
	});
}

/**
 * Check if a price represents an upgrade cost in gift redemption mode
 */
function isUpgradeCost(money?: Money): boolean {
	return Boolean(money && money.dollars > 0);
}

/**
 * Check if a frame should be displayed in gift redemption mode
 * Shows frame if it has a price > 0 OR if it's the gifted frame type with price = 0
 * Requires giftFramingVariantId to have a value
 */
function shouldShowFrameInGiftRedemption(
	giftRedemption: GiftRedemptionState,
	framePrice: Money,
): boolean {
	if (framePrice.dollars > 0) {
		return true;
	}

	if (framePrice.dollars === 0) {
		return (
			giftRedemption.giftFramingType === "frame" &&
			giftRedemption.giftFramingVariantId !== undefined
		);
	}

	return false;
}

/**
 * Check if hanging strips should be displayed in gift redemption mode
 * Shows hanging strips if they have a price > 0 OR if they're the gifted hanging type with price = 0
 * Requires giftFramingVariantId to have a value
 */
function shouldShowHangingStripsInGiftRedemption(
	giftRedemption: GiftRedemptionState,
	hangingStripsPrice: Money,
): boolean {
	if (hangingStripsPrice.dollars > 0) {
		return true;
	}

	if (hangingStripsPrice.dollars === 0) {
		return (
			giftRedemption.giftFramingType === "nano-strips" &&
			giftRedemption.giftFramingVariantId !== undefined
		);
	}

	return false;
}

/**
 * Build Shopify path for gift redemption with all necessary parameters
 */
function buildShopifyGiftRedemptionPath(
	giftRedemption: GiftRedemptionParams,
	completionData: readonly CartItem[],
): string {
	// Get the current variant ID from completion data (first item is the main product)
	const currentVariantId = completionData[0]?.variantId;
	if (!currentVariantId) {
		throw new Error("No current variant ID found in completion data");
	}

	// Build path with parameters
	const params = new URLSearchParams({
		sessionId: giftRedemption.sessionId,
		variantId: currentVariantId,
	});

	// Add framing variant - use original gift framing if present, otherwise look for frame added during redemption
	if (giftRedemption.giftFramingVariantId) {
		// Use original gift framing
		params.append("framingVariantId", giftRedemption.giftFramingVariantId);
		if (giftRedemption.giftFramingVariantQty) {
			params.append(
				"framingVariantQty",
				giftRedemption.giftFramingVariantQty.toString(),
			);
		}
	} else {
		// No original gift framing - look for frame added during redemption
		// Frame variants are typically items that are not the main product and don't have properties
		const frameItem = completionData.find((item, index) => {
			// Skip the first item (main product) and look for items without properties
			return index > 0 && !item.properties;
		});

		if (frameItem) {
			// Use the frame that was added during redemption
			params.append("framingVariantId", frameItem.variantId);
			params.append("framingVariantQty", frameItem.quantity.toString());
		}
	}

	// Add product properties from completion data
	if (completionData) {
		completionData.forEach((item) => {
			Object.entries(item.properties || {}).forEach(([key, value]) => {
				params.append(`customProperties[${key}]`, value);
			});
		});
	}
	return `/apps/ribbon/brickme?${params.toString()}`;
}

// Export all functions at the bottom
export {
	findFrameWithGiftVariant,
	isUpgradeCost,
	shouldShowFrameInGiftRedemption,
	shouldShowHangingStripsInGiftRedemption,
	buildShopifyGiftRedemptionPath,
};
